import { useMemo, type ReactNode } from 'react';

import { useColorMode } from '@/features/storefront/colorMode/ColorModeContext';
import { StorefrontContainer } from '@/features/storefront/components/StorefrontContainer';
import { flipColors } from '@/features/storefront/colorMode/palette';
import { PART_COMPONENTS } from '@/features/storefront/parts/registry';
import { PartsContext, usePartsEnvironment, type PartsEnvironment } from '@/features/storefront/parts/context';
import { partIsShown, partStyle } from '@/features/storefront/parts/style';
import { partFlag, partsInRegion, type PagePart, type PartType } from '@/features/storefront/sections/parts';
import { cn } from '@/lib/cn';

/**
 * One region of a page, drawn from the parts the merchant put in it.
 *
 * This is the whole of the rendering half of the feature. A theme's product
 * view stops writing `<BuyBox/>` and writes `<PartRegion name="main"/>`; the
 * cart page stops writing its totals in order and writes the same thing with
 * its own nodes handed over as slots. Both then render whatever the merchant
 * arranged, in the theme's own column, at the theme's own width.
 *
 * Rendering a region rather than a page is what keeps a theme a theme. The
 * merchant decides *what is in* the buy column and *in what order*; the theme
 * still decides that there is a buy column, how wide it is, whether it sticks
 * while the pictures scroll, and what the gutter between the two is. Neither
 * side can take the other's decisions, which is why "customise every component"
 * does not end in nine themes that all look the same.
 */
export interface PartRegionProps {
  /** Which region of the current page. */
  name: string;
  /** Wraps the region's parts, for a theme that needs a column of its own. */
  className?: string;
  /**
   * Rendered when the region has nothing in it.
   *
   * A page that draws its own regions passes this directly. A theme's product
   * view does not - it does not know it is being drawn in an editor - so the
   * customiser leaves this off and hands its hints to the environment instead,
   * under {@link PartsEnvironment.emptyRegions}. Either way the shopper's page
   * passes neither and an empty region is simply not drawn.
   */
  empty?: ReactNode;
  /**
   * Draw only these components of the region, or all but these.
   *
   * One caller and one reason. A catalog's filter is a component the merchant
   * orders, hides and styles like any other, but *where* it stands is the
   * theme's: a theme with a filter sidebar wants it in a column beside the grid
   * rather than in the band above it. Rather than making the merchant choose a
   * region whose meaning changes with the theme, the page draws the band
   * without it and the column with only it - and the merchant's control over
   * the component is untouched either way.
   */
  only?: readonly PartType[];
  omit?: readonly PartType[];
  /**
   * For a region drawn at the window's width rather than in a column: each
   * component stands in the page's own column, unless it brings one itself
   * (`BRINGS_ITS_OWN_COLUMN`). Without it a folded description under a
   * product ran from one edge of the screen to the other.
   */
  contain?: boolean;
}

/** The components that draw the page's column themselves, wherever they stand. */
const BRINGS_ITS_OWN_COLUMN: readonly PartType[] = ['product_related'];

export function PartRegion({ name, className, empty, only, omit, contain }: PartRegionProps) {
  const environment = usePartsEnvironment();

  const parts = useMemo(
    () => (environment ? partsInRegion(environment.parts, environment.anchor, name) : []),
    [environment, name],
  );

  if (!environment) return null;

  const shown = parts
    .filter((part) => (only ? only.includes(part.type) : true))
    .filter((part) => (omit ? !omit.includes(part.type) : true))
    .filter((part) => partIsShown(part, environment.device, environment.includeHidden ?? false));
  if (shown.length === 0) return <>{empty ?? environment.emptyRegions?.[name] ?? null}</>;

  // `min-w-0` because a region is nearly always the wide half of a two-column
  // page - the cart's lines beside its summary, the checkout's form beside
  // its - and a grid child's default `min-width: auto` is the min-content
  // width of whatever is inside it. One long order number or discount code
  // then sets the width of the column, the page, and on a phone the screen,
  // which slides sideways. Nothing in a region wants that floor.
  return (
    <div className={cn('min-w-0', className)}>
      {shown.map((part) => (
        <PartView
          key={part.id}
          part={part}
          environment={environment}
          contain={contain && !BRINGS_ITS_OWN_COLUMN.includes(part.type)}
        />
      ))}
    </div>
  );
}

/**
 * One part, with its own presentation applied and its content resolved.
 *
 * Content comes from one of two places, and the order is deliberate: a slot the
 * page handed over wins over anything the registry could draw. That is what
 * lets the checkout page keep its form - live validation, saved addresses,
 * payment selection and all - while still being a row in the merchant's tree.
 */
function PartView({
  part: given,
  environment,
  contain,
}: {
  part: PagePart;
  environment: PartsEnvironment;
  contain?: boolean;
}) {
  // The part's own hex colours, carried into the colour mode plugin's other mode.
  const { flipped } = useColorMode();
  const part = useMemo(() => (flipped ? flipColors(given) : given), [given, flipped]);
  const { className, style } = partStyle(part, environment.presentation, environment.device);
  const slot = environment.slots?.[part.type];
  const Component = PART_COMPONENTS[part.type];

  const content = slot ?? (Component ? <Component part={part} presentation={environment.presentation} /> : null);
  if (content === null || content === undefined) return null;

  const rendered = (
    <div
      id={`part-${part.id}`}
      // What it is, for the few rules a whole shop applies to one kind of
      // component - the app bar's buy bar standing in for the page's buttons.
      data-part={part.type}
      className={cn(
        className,
        // A gallery asked to stay in view sticks inside its region, which a
        // theme's two-column grid stretches to the height of the buy column
        // beside it. Desktop only: on a phone the columns stack.
        part.type === 'product_gallery' && partFlag(part, 'sticky') && 'lg:sticky lg:top-24',
        // The customiser dims what the merchant took down rather than dropping
        // it: they have to be able to see it in order to put it back.
        environment.includeHidden && !part.visible && 'opacity-40',
      )}
      style={style}
    >
      {contain ? <StorefrontContainer>{content}</StorefrontContainer> : content}
    </div>
  );

  return <>{environment.wrap ? environment.wrap(part, rendered) : rendered}</>;
}

/**
 * The provider a page wraps its anchor's content in.
 *
 * A component rather than a bare context so the shopper's pages read the way
 * the storefront reads elsewhere, and so the one thing every caller forgets -
 * memoising the value - happens once here instead of in six pages.
 */
export function PartsProvider({
  children,
  ...environment
}: PartsEnvironment & { children: ReactNode }) {
  const value = useMemo(
    () => environment,
    // Spread into a stable list rather than depending on the object, which a
    // caller rebuilds on every render and which would otherwise re-render every
    // part in the page on every keystroke in the customiser.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      environment.anchor,
      environment.parts,
      environment.presentation,
      environment.device,
      environment.includeHidden,
      environment.slots,
      environment.emptyRegions,
      environment.wrap,
      environment.product,
    ],
  );

  return <PartsContext.Provider value={value}>{children}</PartsContext.Provider>;
}
